import React from 'react'
import {useEffect, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import MinimizedBubble from './MinimizedAIDAWindow'
import AIDAChatWindow from './AIDAChatWindow'
import {useAppState} from '../../../state'
import {AIDASource} from 'state/aida/state'
import styled from 'styled-components'
import {useCurrentRoute} from 'react-navi'

const AUTO_CLOSE_MINUTES = 30

const createChatId = () => {
  if (typeof window !== 'undefined' && window.crypto?.getRandomValues) {
    return Array.from(window.crypto.getRandomValues(new Uint32Array(4)))
      .map(value => value.toString(36))
      .join('')
  }

  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`
}

export type AIDAMessage = {
  role: 'user' | 'assistant'
  content: string
  sources?: AIDASource[]
}

const AIDAWidget = () => {
  const {t, i18n} = useTranslation('aida')
  const [isOpen, setIsOpen] = useState(false)
  const createOpeningAssistantMessage = (): AIDAMessage => ({
    role: 'assistant',
    content: t('aida:openingMessage', 'Hei! Olen virtuaaliassistentti AIDA. Miten voin auttaa?'),
    sources: []
  })

  const [conversation, setConversation] = useState<AIDAMessage[]>([])
  const [chatId, setChatId] = useState<string | null>(null)

  useEffect(() => {
    setConversation(prev => {
      const openingAssistantMessage = createOpeningAssistantMessage()
      if (prev.length === 0) return [openingAssistantMessage]
      if (prev.length === 1 && prev[0].role === 'assistant') return [openingAssistantMessage]
      return prev
    })
    // Re-run when language changes so the opening message updates immediately
  }, [i18n.language])
  const [isLoading, setIsLoading] = useState(false)
  const {actions} = useAppState()
  const route = useCurrentRoute()
  const isHome = route.url.pathname === '/'

  const timerRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (!isOpen) return

    resetInactivityTimer()

    const handleActivity = () => resetInactivityTimer()

    window.addEventListener('click', handleActivity)
    window.addEventListener('mousemove', handleActivity)
    window.addEventListener('keydown', handleActivity)

    return () => {
      window.removeEventListener('click', handleActivity)
      window.removeEventListener('mousemove', handleActivity)
      window.removeEventListener('keydown', handleActivity)
      clearTimeout(timerRef.current!)
    }
  }, [isOpen])

  const resetInactivityTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }
    timerRef.current = setTimeout(() => {
      void resetChatSession(false)
    }, AUTO_CLOSE_MINUTES * 60 * 1000)
  }

  const resetChatSession = async (shouldClearBackend: boolean) => {
    const currentChatId = chatId

    setIsOpen(false)
    setChatId(null)
    setConversation([
      createOpeningAssistantMessage()
    ])

    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }

    if (shouldClearBackend && currentChatId) {
      await actions.v1.aida.clearAIDAConversation(currentChatId)
    }
  }

  const handleClose = async () => {
    await resetChatSession(true)
  }

  const handleMinimize = () => {
    setIsOpen(false)
  }

  const handleMessageSend = async (message: string) => {
    if (isLoading || message.trim() === '') return

    const nextChatId = chatId ?? createChatId()
    if (!chatId) {
      setChatId(nextChatId)
    }

    setConversation(prev => [...prev, {content: message, role: 'user'}])
    setIsLoading(true)
    resetInactivityTimer()

    try {
      const result = await actions.v1.aida.getAIDAResponse({prompt: message, chatId: nextChatId})
      const response: AIDAMessage = {
        content: result.response,
        role: 'assistant',
        sources: result.sources?.map((source, index) => ({
          index,
          source: source.source
        }))
      }
      setConversation(prev => [...prev, response])
    } catch (error) {
      setConversation(prev => [...prev, {content: "Sorry, I couldn't process your request.", role: 'assistant'}])
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <AIDAWidgetContainer>
      {!isOpen ? (
        <MinimizedBubble onClick={() => setIsOpen(true)} />
      ) : (
        <PageWrapper>
          {!isHome && isOpen && <DimOverlay onClick={handleMinimize} />}
          <AIDAChatWindow
            onClose={handleClose}
            onMinimize={handleMinimize}
            conversation={conversation}
            onSendMessage={handleMessageSend}
            isLoading={isLoading}
          />
        </PageWrapper>
      )}
    </AIDAWidgetContainer>
  )
}

const AIDAWidgetContainer = styled.div`
  position: fixed;
  bottom: 0;
  right: -20px;
  margin-right: 4%;
  z-index: 4000;
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const PageWrapper = styled.div`
  position: relative;
`

const DimOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 3000; /* above content */
`

export default AIDAWidget
